import React, { useEffect, useState } from "react";
import { Navigate, Link as RouterLink, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { useTranslation } from "react-i18next";
import {
  getOrganization,
  getOrganizationMemberships,
  getOrganizationPaymentAccount,
  getOrganizationStorefront,
  inviteOrganizationMember,
  patchOrganization,
  patchOrganizationMember,
  deleteOrganizationMember,
  patchOrganizationStorefront,
  putOrganizationPaymentAccount,
  uploadOrganizationMedia,
} from "../../api/organizations";
import { getRoutes, patchRoute } from "../../api/routes";
import {
  getOrganizationId,
  getUserEmail,
  isPlatformAdminUser,
  isTenantAdminUser,
} from "../../utils/auth";

const PLATFORM_TABS = ["identity", "storefront", "inventory", "legal", "staff", "payments"];
const TENANT_TABS = ["identity", "storefront", "legal", "staff"];

const TENANT_STAFF_ROLES = [
  "TENANT_ADMIN",
  "DRIVER",
  "CASHIER",
  "CARRIER_MANAGER",
];

const TenantDetailPage = () => {
  const { t } = useTranslation("common");
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isPlatform = isPlatformAdminUser();
  const tabKeys = isPlatform ? PLATFORM_TABS : TENANT_TABS;
  const tabParam = searchParams.get("tab");
  const tab = tabKeys.includes(tabParam) ? tabKeys.indexOf(tabParam) : 0;
  const [error, setError] = useState("");
  const [org, setOrg] = useState(null);
  const [storefront, setStorefront] = useState(null);
  const [pay, setPay] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("TENANT_ADMIN");
  const [invitePassword, setInvitePassword] = useState("");
  const [privateKey, setPrivateKey] = useState("");
  const [routes, setRoutes] = useState([]);
  const [assignRouteId, setAssignRouteId] = useState("");
  const activeKey = tabKeys[tab] || tabKeys[0];
  const myEmail = (getUserEmail() || "").toLowerCase();
  const staffRoleOptions =
    isPlatform && org?.is_host_tenant
      ? ["PLATFORM_ADMIN", ...TENANT_STAFF_ROLES]
      : TENANT_STAFF_ROLES;

  useEffect(() => {
    const ownId = getOrganizationId();
    if (isPlatform) {
      return;
    }
    if (!isTenantAdminUser() || ownId == null || String(ownId) !== String(id)) {
      navigate("/403", { replace: true });
    }
  }, [id, isPlatform, navigate]);

  const load = async () => {
    setError("");
    try {
      const [o, s, p, m] = await Promise.all([
        getOrganization(id),
        getOrganizationStorefront(id),
        isPlatform
          ? getOrganizationPaymentAccount(id)
          : Promise.resolve({ data: null }),
        getOrganizationMemberships(id),
      ]);
      setOrg(o.data);
      setStorefront(s.data);
      setPay(p.data);
      const rows = m.data;
      setMemberships(Array.isArray(rows) ? rows : rows?.results || []);
    } catch {
      setError(t("admin.tenantsSaveError"));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (activeKey !== "inventory") return;
    getRoutes()
      .then((resp) => setRoutes(resp.data?.results || resp.data || []))
      .catch(() => setError(t("admin.tenantsSaveError")));
  }, [activeKey, t]);

  const saveIdentity = async () => {
    setError("");
    try {
      const payload = {
        display_name: org.display_name,
        legal_name: org.legal_name,
        edrpou: org.edrpou,
        legal_address: org.legal_address,
        contact_email: org.contact_email,
        contact_phone: org.contact_phone,
      };
      if (isPlatform) {
        payload.is_carrier = org.is_carrier;
        payload.is_agent = org.is_agent;
      }
      const resp = await patchOrganization(id, payload);
      setOrg({ ...org, ...resp.data });
    } catch {
      setError(t("admin.tenantsSaveError"));
    }
  };

  const saveStorefront = async () => {
    setError("");
    try {
      const resp = await patchOrganizationStorefront(id, {
        theme: storefront.theme,
        branding: storefront.branding,
        homepage: storefront.homepage,
      });
      setStorefront(resp.data);
    } catch {
      setError(t("admin.tenantsSaveError"));
    }
  };

  const moveBlock = (index, dir) => {
    const blocks = [...(storefront.homepage?.blocks || [])];
    const next = index + dir;
    if (next < 0 || next >= blocks.length) return;
    const tmp = blocks[index];
    blocks[index] = blocks[next];
    blocks[next] = tmp;
    setStorefront({
      ...storefront,
      homepage: { ...storefront.homepage, blocks },
    });
  };

  const toggleBlock = (index, enabled) => {
    const blocks = [...(storefront.homepage?.blocks || [])];
    blocks[index] = { ...blocks[index], enabled };
    setStorefront({
      ...storefront,
      homepage: { ...storefront.homepage, blocks },
    });
  };

  const setBlockProp = (index, key, value) => {
    const blocks = [...(storefront.homepage?.blocks || [])];
    blocks[index] = {
      ...blocks[index],
      props: { ...(blocks[index].props || {}), [key]: value },
    };
    setStorefront({
      ...storefront,
      homepage: { ...storefront.homepage, blocks },
    });
  };

  const setBrandingContact = (key, value) => {
    const branding = storefront.branding || {};
    const contacts = { ...(branding.contacts || {}), [key]: value };
    setStorefront({
      ...storefront,
      branding: { ...branding, contacts, support_email: key === "email" ? value : branding.support_email },
    });
  };

  const onUpload = async (kind, file) => {
    if (!file) return;
    setError("");
    try {
      const resp = await uploadOrganizationMedia(id, kind, file);
      setStorefront(resp.data);
    } catch {
      setError(t("admin.tenantsSaveError"));
    }
  };

  const inviteStaff = async () => {
    setError("");
    setInvitePassword("");
    try {
      const resp = await inviteOrganizationMember(id, {
        email: inviteEmail.trim(),
        role: inviteRole,
      });
      setMemberships((rows) => {
        const without = rows.filter((r) => r.id !== resp.data.id);
        return [...without, resp.data];
      });
      if (resp.data.temporary_password) {
        setInvitePassword(resp.data.temporary_password);
      }
      setInviteEmail("");
    } catch (err) {
      setError(err?.response?.data?.detail || t("admin.tenantsSaveError"));
    }
  };

  const changeStaffRole = async (membershipId, role) => {
    setError("");
    try {
      const resp = await patchOrganizationMember(id, membershipId, { role });
      setMemberships((rows) =>
        rows.map((row) => (row.id === membershipId ? resp.data : row))
      );
    } catch (err) {
      setError(err?.response?.data?.detail || t("admin.tenantsStaffUpdateError"));
    }
  };

  const removeStaff = async (row) => {
    if (!window.confirm(t("admin.tenantsStaffRemoveConfirm", { email: row.email }))) {
      return;
    }
    setError("");
    try {
      await deleteOrganizationMember(id, row.id);
      setMemberships((rows) => rows.filter((r) => r.id !== row.id));
    } catch (err) {
      setError(err?.response?.data?.detail || t("admin.tenantsStaffRemoveError"));
    }
  };

  const openSite = () => {
    if (!org?.platform_hostname) return;
    window.open(`https://${org.platform_hostname}/`, "_blank", "noopener,noreferrer");
  };

  const copyHostname = async () => {
    if (!org?.platform_hostname) return;
    try {
      await navigator.clipboard.writeText(org.platform_hostname);
    } catch {
      /* ignore */
    }
  };

  const assignRoute = async () => {
    if (!assignRouteId) return;
    setError("");
    try {
      await patchRoute(assignRouteId, { owner_organization: Number(id) });
      const resp = await getRoutes();
      setRoutes(resp.data?.results || resp.data || []);
      setAssignRouteId("");
    } catch {
      setError(t("admin.tenantsSaveError"));
    }
  };

  const unassignRoute = async (routeId) => {
    setError("");
    try {
      await patchRoute(routeId, { owner_organization: null });
      const resp = await getRoutes();
      setRoutes(resp.data?.results || resp.data || []);
    } catch {
      setError(t("admin.tenantsSaveError"));
    }
  };

  const setLegalPage = (key, value) => {
    setStorefront({
      ...storefront,
      legal_pages: { ...(storefront.legal_pages || {}), [key]: value },
    });
  };

  const saveLegal = async () => {
    setError("");
    try {
      const resp = await patchOrganizationStorefront(id, {
        legal_pages: storefront.legal_pages || {},
      });
      setStorefront(resp.data);
    } catch {
      setError(t("admin.tenantsSaveError"));
    }
  };

  const savePay = async () => {
    setError("");
    try {
      const payload = {
        public_key: pay.public_key,
        is_active: pay.is_active,
      };
      if (privateKey.trim()) payload.private_key = privateKey.trim();
      const resp = await putOrganizationPaymentAccount(id, payload);
      setPay(resp.data);
      setPrivateKey("");
    } catch {
      setError(t("admin.tenantsSaveError"));
    }
  };

  if (!org || !storefront) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        {error ? <Alert severity="error">{error}</Alert> : <Typography>{t("admin.loading")}</Typography>}
      </Container>
    );
  }

  const colors = storefront.theme?.colors || {};
  const blocks = storefront.homepage?.blocks || [];

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button
        component={RouterLink}
        to={isPlatform ? "/administrator/tenants" : "/administrator/dashboard"}
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 2 }}
      >
        {isPlatform ? t("admin.tenantsBack") : t("header.adminPanel")}
      </Button>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        {org.display_name || org.name} ({org.slug})
      </Typography>
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      <Tabs
        value={tab}
        onChange={(_e, v) => {
          const key = tabKeys[v];
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              if (!key || v === 0) next.delete("tab");
              else next.set("tab", key);
              return next;
            },
            { replace: true }
          );
        }}
        sx={{ mb: 2 }}
      >
        {tabKeys.map((key) => (
          <Tab
            key={key}
            label={t(
              {
                identity: "admin.tenantsTabIdentity",
                storefront: "admin.tenantsTabStorefront",
                inventory: "admin.tenantsTabInventory",
                legal: "admin.tenantsTabLegal",
                staff: "admin.tenantsTabStaff",
                payments: "admin.tenantsTabPayments",
              }[key]
            )}
          />
        ))}
      </Tabs>

      {activeKey === "identity" && (
        <Stack spacing={2}>
          {org.platform_hostname ? (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography color="text.secondary">
                {t("admin.tenantsDomain")}: {org.platform_hostname}
              </Typography>
              <Button size="small" variant="outlined" onClick={copyHostname}>
                {t("admin.tenantsCopyDomain")}
              </Button>
              <Button size="small" variant="contained" onClick={openSite}>
                {t("admin.tenantsOpenSite")}
              </Button>
            </Stack>
          ) : null}
          <TextField
            label={t("admin.tenantsDisplayName")}
            value={org.display_name || ""}
            onChange={(e) => setOrg({ ...org, display_name: e.target.value })}
          />
          <TextField
            label={t("admin.tenantsLegalName")}
            value={org.legal_name || ""}
            onChange={(e) => setOrg({ ...org, legal_name: e.target.value })}
          />
          <TextField
            label={t("admin.tenantsEdrpou")}
            value={org.edrpou || ""}
            onChange={(e) => setOrg({ ...org, edrpou: e.target.value })}
          />
          <TextField
            label={t("admin.tenantsLegalAddress")}
            value={org.legal_address || ""}
            onChange={(e) => setOrg({ ...org, legal_address: e.target.value })}
            multiline
            minRows={2}
          />
          <TextField
            label={t("admin.tenantsSlug")}
            value={org.slug || ""}
            disabled
          />
          <TextField
            label="Email"
            value={org.contact_email || ""}
            onChange={(e) => setOrg({ ...org, contact_email: e.target.value })}
          />
          <TextField
            label="Phone"
            value={org.contact_phone || ""}
            onChange={(e) => setOrg({ ...org, contact_phone: e.target.value })}
          />
          {isPlatform ? (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(org.is_carrier)}
                    onChange={(e) => setOrg({ ...org, is_carrier: e.target.checked })}
                  />
                }
                label={t("admin.tenantsCarrier")}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(org.is_agent)}
                    onChange={(e) => setOrg({ ...org, is_agent: e.target.checked })}
                  />
                }
                label={t("admin.tenantsAgent")}
              />
            </>
          ) : null}
          <Button variant="contained" onClick={saveIdentity}>
            {t("admin.tenantsSave")}
          </Button>
        </Stack>
      )}

      {activeKey === "storefront" && (
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              label={t("admin.tenantsPrimary")}
              value={colors.primary || ""}
              onChange={(e) =>
                setStorefront({
                  ...storefront,
                  theme: {
                    ...storefront.theme,
                    colors: { ...colors, primary: e.target.value },
                  },
                })
              }
              sx={{ flex: 1 }}
            />
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: colors.primary || "transparent",
              }}
            />
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              label={t("admin.tenantsAccent")}
              value={colors.accent || ""}
              onChange={(e) =>
                setStorefront({
                  ...storefront,
                  theme: {
                    ...storefront.theme,
                    colors: { ...colors, accent: e.target.value },
                  },
                })
              }
              sx={{ flex: 1 }}
            />
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: colors.accent || "transparent",
              }}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <Button component="label" variant="outlined">
              {t("admin.tenantsLogo")}
              <input
                hidden
                type="file"
                accept="image/*"
                onChange={(e) => onUpload("logo", e.target.files?.[0])}
              />
            </Button>
            <Button component="label" variant="outlined">
              {t("admin.tenantsFavicon")}
              <input
                hidden
                type="file"
                accept="image/*,.ico"
                onChange={(e) => onUpload("favicon", e.target.files?.[0])}
              />
            </Button>
            <Button component="label" variant="outlined">
              {t("admin.tenantsHero")}
              <input
                hidden
                type="file"
                accept="image/*"
                onChange={(e) => onUpload("hero", e.target.files?.[0])}
              />
            </Button>
          </Stack>
          <Typography fontWeight={600}>{t("admin.tenantsContacts")}</Typography>
          <TextField
            label={t("admin.tenantsContactEmail")}
            value={storefront.branding?.contacts?.email || storefront.branding?.support_email || ""}
            onChange={(e) => setBrandingContact("email", e.target.value)}
          />
          <TextField
            label={t("admin.tenantsPhones")}
            multiline
            minRows={2}
            value={(storefront.branding?.contacts?.phones || [])
              .map((p) => p.display || p.tel || "")
              .join("\n")}
            onChange={(e) => {
              const phones = e.target.value
                .split("\n")
                .map((line) => line.trim())
                .filter(Boolean)
                .map((line) => ({ display: line, tel: line.replace(/\s+/g, "") }));
              setBrandingContact("phones", phones);
            }}
          />
          <TextField
            label={t("admin.tenantsTelegram")}
            value={storefront.branding?.contacts?.telegram || ""}
            onChange={(e) => setBrandingContact("telegram", e.target.value)}
          />
          <TextField
            label={t("admin.tenantsInstagram")}
            value={storefront.branding?.contacts?.instagram || ""}
            onChange={(e) => setBrandingContact("instagram", e.target.value)}
          />
          <TextField
            label={t("admin.tenantsWhatsapp")}
            value={storefront.branding?.contacts?.whatsapp || ""}
            onChange={(e) => setBrandingContact("whatsapp", e.target.value)}
          />
          <TextField
            label={t("admin.tenantsFacebook")}
            value={storefront.branding?.contacts?.facebook || ""}
            onChange={(e) => setBrandingContact("facebook", e.target.value)}
          />
          <Typography fontWeight={600}>{t("admin.tenantsBlocks")}</Typography>
          {blocks.map((block, index) => (
            <Paper key={block.id || block.type} variant="outlined" sx={{ p: 1.5 }}>
              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography sx={{ flex: 1 }}>{block.type}</Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={block.enabled !== false}
                        disabled={block.type === "search"}
                        onChange={(e) => toggleBlock(index, e.target.checked)}
                      />
                    }
                    label={t("admin.tenantsBlockOn")}
                  />
                  <Button size="small" onClick={() => moveBlock(index, -1)} disabled={index === 0}>
                    <ArrowUpwardIcon fontSize="small" />
                  </Button>
                  <Button
                    size="small"
                    onClick={() => moveBlock(index, 1)}
                    disabled={index === blocks.length - 1}
                  >
                    <ArrowDownwardIcon fontSize="small" />
                  </Button>
                </Stack>
                {block.type === "hero" ? (
                  <Stack spacing={1}>
                    <TextField
                      size="small"
                      label={t("admin.tenantsHeroTitle")}
                      value={block.props?.title || ""}
                      onChange={(e) => setBlockProp(index, "title", e.target.value)}
                    />
                    <TextField
                      size="small"
                      label={t("admin.tenantsHeroSubtitle")}
                      value={block.props?.subtitle || ""}
                      onChange={(e) => setBlockProp(index, "subtitle", e.target.value)}
                    />
                  </Stack>
                ) : null}
              </Stack>
            </Paper>
          ))}
          <Button variant="contained" onClick={saveStorefront}>
            {t("admin.tenantsSave")}
          </Button>
        </Stack>
      )}

      {activeKey === "inventory" && (
        <Stack spacing={2}>
          <Typography color="text.secondary">{t("admin.tenantsInventoryHint")}</Typography>
          <Typography fontWeight={600}>{t("admin.tenantsInventoryAssigned")}</Typography>
          {routes.filter((r) => r.owner_organization === Number(id)).length === 0 ? (
            <Typography color="text.secondary">{t("admin.tenantsInventoryEmpty")}</Typography>
          ) : (
            routes
              .filter((r) => r.owner_organization === Number(id))
              .map((route) => (
                <Paper key={route.id} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography>{route.name}</Typography>
                    <Button size="small" onClick={() => unassignRoute(route.id)}>
                      {t("admin.tenantsInventoryUnassign")}
                    </Button>
                  </Stack>
                </Paper>
              ))
          )}
          <Typography fontWeight={600}>{t("admin.tenantsInventoryAssign")}</Typography>
          <TextField
            select
            label={t("admin.tenantsInventoryRoute")}
            value={assignRouteId}
            onChange={(e) => setAssignRouteId(e.target.value)}
          >
            {routes
              .filter((r) => r.owner_organization !== Number(id))
              .map((route) => (
                <MenuItem key={route.id} value={String(route.id)}>
                  {route.name}
                </MenuItem>
              ))}
          </TextField>
          <Button variant="contained" onClick={assignRoute} disabled={!assignRouteId}>
            {t("admin.tenantsInventoryAssignBtn")}
          </Button>
        </Stack>
      )}

      {activeKey === "legal" && (
        <Stack spacing={2}>
          <Typography color="text.secondary">{t("admin.tenantsLegalHint")}</Typography>
          <TextField
            label={t("admin.tenantsLegalPrivacy")}
            multiline
            minRows={4}
            value={storefront.legal_pages?.privacy || ""}
            onChange={(e) => setLegalPage("privacy", e.target.value)}
          />
          <TextField
            label={t("admin.tenantsLegalTerms")}
            multiline
            minRows={4}
            value={storefront.legal_pages?.terms || ""}
            onChange={(e) => setLegalPage("terms", e.target.value)}
          />
          <TextField
            label={t("admin.tenantsLegalOffer")}
            multiline
            minRows={4}
            value={storefront.legal_pages?.offer || ""}
            onChange={(e) => setLegalPage("offer", e.target.value)}
          />
          <Button variant="contained" onClick={saveLegal}>
            {t("admin.tenantsSave")}
          </Button>
        </Stack>
      )}

      {activeKey === "staff" && (
        <Stack spacing={2}>
          <Typography fontWeight={600}>{t("admin.tenantsStaff")}</Typography>
          {memberships.length === 0 ? (
            <Typography color="text.secondary">{t("admin.tenantsStaffEmpty")}</Typography>
          ) : null}
          {memberships.map((row) => {
            const isSelf = (row.email || "").toLowerCase() === myEmail;
            const roleChoices =
              row.role === "PLATFORM_ADMIN" && !staffRoleOptions.includes("PLATFORM_ADMIN")
                ? ["PLATFORM_ADMIN", ...staffRoleOptions]
                : staffRoleOptions;
            return (
              <Paper key={row.id} variant="outlined" sx={{ p: 1.5 }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  alignItems={{ sm: "center" }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography>{row.email}</Typography>
                    {row.is_active === false ? (
                      <Typography variant="body2" color="text.secondary">
                        {t("admin.tenantsStaffInactive")}
                      </Typography>
                    ) : null}
                  </Box>
                  <TextField
                    select
                    size="small"
                    label={t("admin.tenantsStaffRole")}
                    value={row.role}
                    onChange={(e) => changeStaffRole(row.id, e.target.value)}
                    sx={{ minWidth: 200 }}
                    disabled={row.role === "PLATFORM_ADMIN" && !isPlatform}
                  >
                    {roleChoices.map((role) => (
                      <MenuItem key={role} value={role}>
                        {t(`admin.staffRole.${role}`, { defaultValue: role })}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Button
                    color="error"
                    variant="outlined"
                    onClick={() => removeStaff(row)}
                    disabled={isSelf}
                  >
                    {t("admin.tenantsStaffRemove")}
                  </Button>
                </Stack>
              </Paper>
            );
          })}
          <TextField
            label={t("admin.tenantsStaffEmail")}
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
          />
          <TextField
            select
            label={t("admin.tenantsStaffRole")}
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value)}
          >
            {staffRoleOptions.map((role) => (
              <MenuItem key={role} value={role}>
                {t(`admin.staffRole.${role}`, { defaultValue: role })}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="contained" onClick={inviteStaff} disabled={!inviteEmail.trim()}>
            {t("admin.tenantsStaffInvite")}
          </Button>
          {invitePassword ? (
            <Alert severity="success">
              {t("admin.tenantsStaffTempPassword")}: {invitePassword}
            </Alert>
          ) : null}
        </Stack>
      )}

      {activeKey === "payments" && pay && (
        <Stack spacing={2}>
          <Typography>
            {t("admin.tenantsCardPayment")}:{" "}
            {pay.card_payment ? t("admin.tenantsCardOn") : t("admin.tenantsCardOff")}
          </Typography>
          <TextField
            label={t("admin.tenantsLiqpayPublic")}
            value={pay.public_key || ""}
            onChange={(e) => setPay({ ...pay, public_key: e.target.value })}
          />
          <TextField
            label={t("admin.tenantsLiqpayPrivate")}
            type="password"
            value={privateKey}
            onChange={(e) => setPrivateKey(e.target.value)}
            helperText={pay.has_private_key ? "••••" : ""}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(pay.is_active)}
                onChange={(e) => setPay({ ...pay, is_active: e.target.checked })}
              />
            }
            label={t("admin.tenantsLiqpayActive")}
          />
          <Button variant="contained" onClick={savePay}>
            {t("admin.tenantsSave")}
          </Button>
        </Stack>
      )}
    </Container>
  );
};

export const MyOrganizationRedirect = () => {
  const id = getOrganizationId();
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();
  const suffix = qs ? `?${qs}` : "";
  if (id == null) {
    if (isPlatformAdminUser()) {
      return <Navigate to="/administrator/tenants" replace />;
    }
    return <Navigate to="/403" replace />;
  }
  if (!isTenantAdminUser() && !isPlatformAdminUser()) {
    return <Navigate to="/403" replace />;
  }
  return <Navigate to={`/administrator/tenants/${id}${suffix}`} replace />;
};

export default TenantDetailPage;
